import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'

import { defineListener, errorView, type ErrorView, type ModuleListener } from '@krp/client-runtime'

import { classifyCommandError, type CommandFailure } from '../../../shared/model/commandOutcome'
import {
  buildAllocations,
  buildInvoiceFill,
  buildLineUpdate,
  postingStatusCheckRequested,
  supplyCommandRequested,
  supplyCommandResendRequested,
  type Built,
} from './commands'
import { commandKey, lineKey, normalizeRid } from './keys'
import type { PortOutcome, SuppliesPorts, SuppliesPortsFactory } from './ports'
import {
  allocationDraftRebased,
  allocationDraftRetried,
  allocationEdited,
  allocationSaveSettled,
  allocationSaveStarted,
  commandSettled,
  commandResent,
  commandStarted,
  formChanged,
  headerDiscarded,
  lineDraftRebased,
  lineDraftRetried,
  lineEdited,
  lineSaveSettled,
  lineSaveStarted,
  postingNotObserved,
  postingObserved,
  pricesCleared,
} from './suppliesSlice'
import type {
  AllocationDraft,
  LineDraft,
  SentIdentity,
  SuppliesIntentState,
  SupplyCommand,
  SupplyCommandResult,
} from './types'

/**
 * Коды службы поставок, при которых нужна воля человека, а не повтор: версия
 * строки/шапки устарела (кто-то правил), количество ниже распределённого без
 * политики. Экран показывает серверную версию рядом с набранным.
 */
const CONFLICT_CODES = new Set(['version-conflict', 'concurrent-update', 'allocation-exceeds-quantity', 'configuration-version-mismatch'])

export function classifySupplyError(raw: unknown): CommandFailure {
  const failure = classifyCommandError(raw)
  // 2xx, которое не разобралось: служба команду ПРИНЯЛА — итог неизвестен, а не «не выполнено».
  if (failure.error.kind === 'invalid-response') return { status: 'unknown', resendable: true, error: failure.error }
  if (CONFLICT_CODES.has(failure.error.code)) return { status: 'conflict', resendable: false, error: failure.error }
  return failure
}

const busy = (command: SupplyCommand | undefined) => command !== undefined && (command.status === 'sending' || command.status === 'unknown')

interface Scenario {
  ports: SuppliesPorts
  dispatch: Dispatch<UnknownAction>
  intent: () => SuppliesIntentState
}

const owns = (scenario: Scenario, command: SupplyCommand) => {
  const current = scenario.intent().commands[command.key]
  return current !== undefined && current.operationId === command.operationId && current.status === 'sending'
}

function identityOf(command: SupplyCommand) {
  return {
    operationId: command.operationId,
    idempotencyKey: command.idempotencyKey,
    expectedConfigurationVersion: command.expectedConfigurationVersion,
  }
}

/** Последствия подтверждённой команды для намерения: черновик, который она сохранила, закрыт. */
function afterSuccess(scenario: Scenario, command: SupplyCommand, result: SupplyCommandResult) {
  const ref = { resourceId: command.resourceId, supplyId: command.supplyId }
  if (command.kind === 'update') scenario.dispatch(headerDiscarded(ref))
  if (command.kind === 'set-prices') scenario.dispatch(pricesCleared(ref))
  if (command.kind === 'create' && command.invoiceFileId !== null && result.kind === 'created') fillCreatedFromInvoice(scenario, command, result.supply.id, command.invoiceFileId)
}

/**
 * Поставка из накладной: создание подтверждено — заполнить её строки из файла. Это обычная команда `import-invoice`
 * новой поставки: её итог («Файл …: добавлено N») и отказ видны в окне поставки, а идентификатор файла уже стоит в
 * форме «Заполнить из накладной» — повторить можно кнопкой, без ручного переноса.
 */
function fillCreatedFromInvoice(scenario: Scenario, command: SupplyCommand, supplyId: string, fileId: string) {
  const ref = { resourceId: command.resourceId, supplyId }
  scenario.dispatch(formChanged({ ...ref, patch: { invoiceFileId: fileId, invoiceMode: 'Append' } }))
  const built = buildInvoiceFill(fileId, 'Append')
  if (!built.ok) return
  scenario.dispatch(
    supplyCommandRequested({ ...ref, kind: 'import-invoice', data: built.data, expectedConfigurationVersion: command.expectedConfigurationVersion }),
  )
}

/** Отправить команду и записать итог. «Неизвестно» не повторяется никогда само: решает человек (ADR-11). */
async function sendAndSettle(scenario: Scenario, command: SupplyCommand) {
  if (!owns(scenario, command)) return
  const outcome = await scenario.ports.send({
    resourceId: command.resourceId,
    supplyId: command.supplyId,
    kind: command.kind,
    lineId: command.lineId,
    data: command.data,
    query: command.query,
    identity: identityOf(command),
  })
  if (outcome.ok) {
    scenario.dispatch(
      commandSettled({ key: command.key, operationId: command.operationId, status: 'succeeded', resendable: false, error: null, result: outcome.value }),
    )
    afterSuccess(scenario, command, outcome.value)
    return
  }
  const failure = classifySupplyError(outcome.error)
  // Проведение не повторяется тем же POST: исход выясняет чтение статуса саги.
  const resendable = command.kind === 'post' ? false : failure.resendable
  scenario.dispatch(
    commandSettled({ key: command.key, operationId: command.operationId, status: failure.status, resendable, error: failure.error, result: null }),
  )
  // Проведение перечитывается всегда: состояние саги решает, что показать дальше.
  if (failure.status !== 'failed' || command.kind === 'post') scenario.ports.reloadSupply(command.resourceId, command.supplyId)
}

// --- автосохранение правок: debounce на строку, не больше одного запроса строки в полёте ---------

interface Saver {
  schedule: (key: string) => void
}

type SavedDraft = LineDraft | AllocationDraft

/** Чем черновик строки отличается от черновика распределения — одна таблица, сценарий общий. */
interface DraftAdapter<D extends SavedDraft> {
  read: (intent: SuppliesIntentState, key: string) => D | undefined
  build: (draft: D) => Built<unknown>
  command: 'update-line' | 'set-line-allocations'
  started: (payload: { key: string; sent: SentIdentity }) => UnknownAction
  settled: (payload: {
    key: string
    revision: number
    ok: boolean
    conflict: boolean
    unknown?: boolean
    error: ErrorView | null
    version: number | null
  }) => UnknownAction
}

const LINE_ADAPTER: DraftAdapter<LineDraft> = {
  read: (intent, key) => intent.lineDrafts[key],
  build: buildLineUpdate,
  command: 'update-line',
  started: lineSaveStarted,
  settled: lineSaveSettled,
}

const ALLOCATION_ADAPTER: DraftAdapter<AllocationDraft> = {
  read: (intent, key) => intent.allocationDrafts[key],
  build: (draft) => buildAllocations(draft.resourceId, draft),
  command: 'set-line-allocations',
  started: allocationSaveStarted,
  settled: allocationSaveSettled,
}

/**
 * Debounce на СТРОКУ, а не на таблицу (как `linesAutoSaveMiddleware` донора):
 * правки разных строк уходят параллельно, а по одной строке в полёте не больше
 * одного запроса — сервер сверяет версию, и второй PATCH по старой версии
 * гарантированно получил бы 409.
 */
function createSaver<D extends SavedDraft>(
  adapter: DraftAdapter<D>,
  scenarioOf: () => Scenario,
  inFlight: Set<string>,
  afterWrite: (key: string) => void,
): Saver {
  const timers = new Map<string, ReturnType<typeof setTimeout>>()

  const schedule = (key: string) => {
    const previous = timers.get(key)
    if (previous !== undefined) clearTimeout(previous)
    timers.set(
      key,
      setTimeout(() => {
        timers.delete(key)
        void submit(key)
      }, scenarioOf().ports.debounceMs),
    )
  }

  async function submit(key: string) {
    const scenario = scenarioOf()
    const draft = adapter.read(scenario.intent(), key)
    // Конфликт и отказ ждут решения человека; удалённый черновик (выход, смена базы) не отправляется вовсе.
    if (draft === undefined || draft.state !== 'pending') return
    // Строка уже в полёте: накопленное уйдёт по её ответу (см. ниже), с новой версией.
    if (inFlight.has(key)) return
    const built = adapter.build(draft)
    if (!built.ok) {
      scenario.dispatch(
        adapter.settled({ key, revision: draft.revision, ok: false, conflict: false, error: errorView({ message: built.message }), version: null }),
      )
      return
    }
    // Тот же набор после неизвестного исхода — та же идентичность: служба отдаст прежний итог, второй записи не будет.
    const previous = draft.sent
    const sent: SentIdentity =
      previous !== null && previous.revision === draft.revision && previous.baseVersion === draft.baseVersion
        ? previous
        : { operationId: scenario.ports.newOperationId(), revision: draft.revision, baseVersion: draft.baseVersion }
    scenario.dispatch(adapter.started({ key, sent }))
    inFlight.add(key)
    let outcome: PortOutcome<SupplyCommandResult>
    try {
      outcome = await scenario.ports.send({
        resourceId: draft.resourceId,
        supplyId: draft.supplyId,
        kind: adapter.command,
        lineId: draft.lineId,
        data: built.data,
        query: null,
        identity: {
          operationId: sent.operationId,
          idempotencyKey: `onec-ui:supplies-${adapter.command}:${sent.operationId}`,
          expectedConfigurationVersion: draft.configurationVersion,
        },
      })
    } finally {
      inFlight.delete(key)
    }
    const after = scenarioOf()
    if (outcome.ok) {
      const version = outcome.value.kind === 'done' ? outcome.value.lineVersion : null
      after.dispatch(adapter.settled({ key, revision: sent.revision, ok: true, conflict: false, error: null, version }))
    } else {
      const failure = classifySupplyError(outcome.error)
      after.dispatch(
        adapter.settled({
          key,
          revision: sent.revision,
          ok: false,
          conflict: failure.status === 'conflict',
          unknown: failure.status === 'unknown',
          error: failure.error,
          version: null,
        }),
      )
      // Конфликт или неизвестный исход: экран должен показать то, что на сервере сейчас.
      if (failure.status === 'conflict' || failure.status === 'unknown') after.ports.reloadSupply(draft.resourceId, draft.supplyId)
    }
    // Пока летел запрос, человек набрал ещё: черновик снова «ожидает» — отправляем накопленное.
    const next = adapter.read(after.intent(), key)
    if (next !== undefined && next.state === 'pending') schedule(key)
    // Соседний вид той же строки (строка ↔ распределение) ждал, пока эта запись в полёте.
    afterWrite(key)
  }

  return { schedule }
}

/** Listeners фичи поставок: команда → порт. Состояние модуля читают только через `intentOf`. */
export function createSuppliesListeners<S>(makePorts: SuppliesPortsFactory, intentOf: (state: S) => SuppliesIntentState): ModuleListener<S>[] {
  let current: { dispatch: Dispatch<UnknownAction>; getModuleState: () => S } | null = null
  const scenarioOf = (api: { dispatch: Dispatch<UnknownAction>; getModuleState: () => S }): Scenario => {
    current = api
    return { ports: makePorts(api.dispatch), dispatch: api.dispatch, intent: () => intentOf(api.getModuleState()) }
  }
  const latest = (): Scenario => {
    if (current === null) throw new Error('listener поставок ещё не получал действий')
    return scenarioOf(current)
  }
  // По строке в полёте не больше ОДНОЙ записи — и правки, и распределения: обе двигают версию строки.
  const inFlight = new Set<string>()
  const savers: { line?: Saver; allocation?: Saver } = {}
  const lineSaver = createSaver(LINE_ADAPTER, latest, inFlight, (key) => {
    if (latest().intent().allocationDrafts[key]?.state === 'pending') savers.allocation?.schedule(key)
  })
  const allocationSaver = createSaver(ALLOCATION_ADAPTER, latest, inFlight, (key) => {
    if (latest().intent().lineDrafts[key]?.state === 'pending') savers.line?.schedule(key)
  })
  savers.line = lineSaver
  savers.allocation = allocationSaver
  const lineKeyOf = (payload: { resourceId: string; supplyId: string; lineId: string }) =>
    lineKey(payload.resourceId, payload.supplyId, payload.lineId)

  return [
    defineListener<S, ReturnType<typeof supplyCommandRequested>>(supplyCommandRequested.match, async (action, api) => {
      const scenario = scenarioOf(api)
      const intent = action.payload
      const supplyId = intent.supplyId.toLowerCase()
      const key = commandKey(intent.resourceId, supplyId, intent.kind, intent.detail)
      if (busy(scenario.intent().commands[key])) return
      const operationId = scenario.ports.newOperationId()
      const command: SupplyCommand = {
        key,
        resourceId: normalizeRid(intent.resourceId),
        supplyId,
        kind: intent.kind,
        lineId: intent.lineId ?? null,
        data: intent.data,
        query: intent.query ?? null,
        operationId,
        // Ключ привязан к операции: одинаковый ключ в двух базах — две операции и на сервере.
        idempotencyKey: `onec-ui:supplies-${intent.kind}:${operationId}`,
        expectedConfigurationVersion: intent.expectedConfigurationVersion,
        status: 'sending',
        resendable: false,
        error: null,
        result: null,
        baseline: intent.baseline ?? null,
        invoiceFileId: intent.kind === 'create' && intent.invoiceFileId !== undefined && intent.invoiceFileId !== '' ? intent.invoiceFileId : null,
      }
      if (command.expectedConfigurationVersion === '') {
        // Ревизии базы нет (первое чтение не удалось): команда не уходит, но человек это видит.
        scenario.dispatch(commandStarted({ ...command, status: 'failed' }))
        scenario.dispatch(
          commandSettled({
            key,
            operationId,
            status: 'failed',
            resendable: false,
            error: errorView({ message: 'Нет ревизии настроек базы: перечитайте поставку и повторите.' }),
            result: null,
          }),
        )
        return
      }
      scenario.dispatch(commandStarted(command))
      await sendAndSettle(scenario, command)
    }),

    defineListener<S, ReturnType<typeof supplyCommandResendRequested>>(supplyCommandResendRequested.match, async (action, api) => {
      const scenario = scenarioOf(api)
      const command = scenario.intent().commands[action.payload.key]
      // Повтор — только явным действием человека, только той же командой и никогда — проведения.
      if (command === undefined || command.status === 'sending' || !command.resendable || command.kind === 'post') return
      scenario.dispatch(commandResent({ key: command.key }))
      await sendAndSettle(scenario, { ...command, status: 'sending' })
    }),

    defineListener<S, ReturnType<typeof postingStatusCheckRequested>>(postingStatusCheckRequested.match, async (action, api) => {
      const scenario = scenarioOf(api)
      const { resourceId, supplyId } = action.payload
      const status = await scenario.ports.readPostingStatus(resourceId, supplyId)
      const command = scenario.intent().commands[commandKey(resourceId, supplyId, 'post')]
      // Журнал саги знает попытку новее той, что была до нажатия «Провести», — проведение началось;
      // не знает — запрос до службы не дошёл. Старую попытку за новую не принимаем.
      if (status.ok && command !== undefined && command.status === 'unknown' && command.baseline !== null) {
        if (status.value.attemptNumber > command.baseline) {
          scenario.dispatch(postingObserved({ resourceId, supplyId, attemptNumber: status.value.attemptNumber }))
        } else if (!status.value.isRunning) {
          scenario.dispatch(postingNotObserved({ resourceId, supplyId }))
        }
      }
      scenario.ports.reloadSupply(resourceId, supplyId)
    }),

    defineListener<S, ReturnType<typeof lineEdited>>(lineEdited.match, (action, api) => {
      scenarioOf(api)
      lineSaver.schedule(lineKeyOf(action.payload))
    }),
    defineListener<S, ReturnType<typeof lineDraftRebased>>(lineDraftRebased.match, (action, api) => {
      scenarioOf(api)
      lineSaver.schedule(lineKeyOf(action.payload))
    }),
    defineListener<S, ReturnType<typeof lineDraftRetried>>(lineDraftRetried.match, (action, api) => {
      scenarioOf(api)
      lineSaver.schedule(lineKeyOf(action.payload))
    }),
    defineListener<S, ReturnType<typeof allocationEdited>>(allocationEdited.match, (action, api) => {
      scenarioOf(api)
      allocationSaver.schedule(lineKeyOf(action.payload))
    }),
    defineListener<S, ReturnType<typeof allocationDraftRebased>>(allocationDraftRebased.match, (action, api) => {
      scenarioOf(api)
      allocationSaver.schedule(lineKeyOf(action.payload))
    }),
    defineListener<S, ReturnType<typeof allocationDraftRetried>>(allocationDraftRetried.match, (action, api) => {
      scenarioOf(api)
      allocationSaver.schedule(lineKeyOf(action.payload))
    }),
  ]
}
